import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api.ts';
import api from '../services/api.ts';

interface User {
    id: number;
    username: string;
    email: string;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (username: string, password: string) => Promise<void>;
    register: (username: string, email: string, password: string) => Promise<void>;
    logout: () => void;
    error: string | null;
    getAuthHeaders: () => Record<string, string>; // New method to get auth headers
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const refreshTimerRef = useRef<number | null>(null);

    // Function to refresh the token
    const refreshToken = async () => {
        try {
            const response = await api.post('/auth/refresh');
            const { access_token } = response.data;
            localStorage.setItem('token', access_token);
            // Schedule the next refresh (5 minutes before expiration)
            scheduleTokenRefresh();
            return access_token;
        } catch (err) {
            console.error('Failed to refresh token:', err);
            // If refresh fails, log the user out
            logout();
            return null;
        }
    };

    // Schedule token refresh 5 minutes before it expires (assuming 30 min expiration)
    const scheduleTokenRefresh = () => {
        // Clear any existing timer
        if (refreshTimerRef.current) {
            window.clearTimeout(refreshTimerRef.current);
        }

        // Set timer to refresh 5 minutes before expiration (25 minutes if token lasts 30)
        const refreshTime = 25 * 60 * 1000; // 25 minutes in milliseconds
        refreshTimerRef.current = window.setTimeout(refreshToken, refreshTime);
    };

    // Setup activity tracking to refresh token when user is active
    useEffect(() => {
        if (!user) return; // Only track activity when user is logged in

        // List of events to track for user activity
        const activityEvents = [
            'mousedown',
            'mousemove',
            'keydown',
            'scroll',
            'touchstart',
            'click',
            'keypress',
        ];

        // Last activity timestamp
        let lastActivity = Date.now();
        // Minimum time between refreshes (5 minutes)
        const minRefreshInterval = 5 * 60 * 1000;

        const handleUserActivity = () => {
            const now = Date.now();
            // Only refresh if it's been at least 5 minutes since the last refresh
            if (now - lastActivity > minRefreshInterval) {
                lastActivity = now;
                // Refresh token when user is active after a period of inactivity
                refreshToken();
            }
        };

        // Add event listeners for user activity
        activityEvents.forEach((event) => {
            window.addEventListener(event, handleUserActivity, { passive: true });
        });

        // Cleanup function
        return () => {
            activityEvents.forEach((event) => {
                window.removeEventListener(event, handleUserActivity);
            });
        };
    }, [user]);

    useEffect(() => {
        // Check if user is logged in on mount
        const token = localStorage.getItem('token');
        if (token) {
            fetchUser(token);
            // Schedule token refresh
            scheduleTokenRefresh();
        } else {
            setIsLoading(false);
        }

        // Cleanup function to clear the timer when component unmounts
        return () => {
            if (refreshTimerRef.current) {
                window.clearTimeout(refreshTimerRef.current);
            }
        };
    }, []);

    const fetchUser = async (token: string) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/auth/me`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setUser(response.data);
        } catch (err) {
            localStorage.removeItem('token');
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    const login = async (username: string, password: string) => {
        setError(null);
        try {
            const formData = new FormData();
            formData.append('username', username);
            formData.append('password', password);
            const response = await axios.post(`${API_BASE_URL}/auth/token`, formData);
            const { access_token } = response.data;

            localStorage.setItem('token', access_token);
            await fetchUser(access_token);
            // Schedule token refresh after successful login
            scheduleTokenRefresh();
        } catch (err) {
            setError('Invalid username or password');
            throw err;
        }
    };

    const register = async (username: string, email: string, password: string) => {
        setError(null);
        try {
            await axios.post(`${API_BASE_URL}/auth/register`, { username, email, password });
            // After registration, log the user in
            await login(username, password);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Registration failed');
            throw err;
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        setUser(null);

        // Clear the refresh timer when logging out
        if (refreshTimerRef.current) {
            window.clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
    };

    // New method to get auth headers for API calls
    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: !!user,
                isLoading,
                login,
                register,
                logout,
                error,
                getAuthHeaders, // Expose the new method
            }}>
            {children}
        </AuthContext.Provider>
    );
};
